import { cva, type VariantProps } from 'class-variance-authority';

import { DARK_CONTROL_TOGGLED } from '../../styles/elevation';

/**
 * Filter-chip visual variants — mirror the Figma "Flashka Design System"
 * file `YZK9r3wDhvw1pY60KlVEIj`, frame `1435:12654` ("Filter chips").
 * Reference nodes:
 *   - 1439:12700 – L / Default / Removable=No / NA   (canonical anatomy)
 *   - 1435:12645 – L / Hover     (bg → #fafafa)
 *   - 1435:12652 – L / Pressed   (bg → secondary-100, shadow dropped, shrink)
 *   - 1435:12630 – L / Focused   (secondary-900 ring @ 1px offset + border
 *                                 lightens to secondary-100)
 *   - 1435:12639 – L / Disabled  (default colors + opacity-50)
 *   - 1435:12631 – L / Removable (symmetric padding + trailing circle-x)
 *   - 1455:14335 – L / Selected / New        (tertiary 50/500/600)
 *   - 1455:14342 – L / Selected / Review     (orange  50/600/600)
 *   - 1455:14349 – L / Selected / Memorised  (green         50/600/600)
 *   - 1455:14356 – L / Selected / To review  (amber   50/600/600)
 *
 * Axes:
 *   tint:      neutral | new | review | memorised | toReview | starred |
 *              primary | pink | rose | lime
 *              — IS the selection axis. `neutral` is the unselected gray
 *              chip; new/review/memorised/toReview are the Figma "Selected"
 *              progress tints. `starred` (amber + Star), `primary` and the
 *              feature hues (pink / rose / lime) are app extensions on the
 *              same -50/-600 ramp — not in the Figma progress-tint set.
 *   size:      xl (44px) | l (40px) | m (36px) | s (32px)
 *   removable: appends a trailing circle-x and switches to symmetric padding
 *   disabled:  opacity treatment (supplied by the Pressable primitive)
 *
 * Anatomy (all tints share this):
 *   – 1px border, per-tint border color.
 *   – Resting drop shadow `shadow-chip` (Figma "Drop small":
 *     `0 2.5px 10px 0 #0000000A`). Dropped on press.
 *   – `backdrop-blur-[7.5px]` (Figma BACKGROUND_BLUR). A visual no-op over
 *     the opaque fill but kept for spec fidelity. Both the shadow and the
 *     blur are `web:`-gated — NativeWind v4's `boxShadow` / `backdrop-blur`
 *     handling on RN 0.83 is unreliable, so the native resting shadow is
 *     applied as an RN `shadowColor`/`elevation` style in `Chip.tsx`.
 *   – gap-1.5 (6px) between icon / label / remove control.
 *
 * States: the design defines the interaction states (hover / pressed /
 * focused / disabled) ONLY on the `neutral` (unselected) chip. The colored
 * "Selected" tints are defined only at rest, so they carry NO per-tint
 * hover/pressed bg — they sit static at their resting color and share the
 * uniform base treatment (press `scale-0.97` + shadow-drop, focus ring). This
 * keeps every "other state" identical across variants, matching the design.
 *   – hover:   NO bg change. Figma's neutral Hover (1435:12645) reuses the
 *              same `secondary/50` fill as Default (1439:12700) — hover is not
 *              a tone step, so we don't add one.
 *   – pressed: (neutral only) bg darkens to secondary-100 (Figma 1435:12652);
 *              for all tints the shared base drops the shadow + applies a 0.97
 *              scale (`web:enabled:active:`).
 *   – focused: 1px `secondary-900` ring at a 1px offset (matches Figma's
 *              outer wrapper border + `p-px` gap). The inner border stays
 *              `secondary-200` — Figma's Focused node (1435:12630) keeps the
 *              resting border and does NOT lighten it. Web-only —
 *              `focus-visible:` is a no-op on RN. Overrides the Pressable
 *              primitive's generic gray ring.
 *   – disabled: `opacity-50` (Figma node 1435:12639). NOTE this differs from
 *              Button's `opacity-30`. Supplied automatically by the Pressable
 *              primitive (web RESET `disabled:opacity-50`; native
 *              `cn(disabled && 'opacity-50')`), so no CVA override is needed.
 *              `enabled:`-scoped hover/active classes don't fire while
 *              disabled.
 *
 * Dark mode: the shared palette inverts every `--{name}-{shade}` via CSS vars
 * in `.dark` / `.dark:root` (generate-css-vars.cjs: dark `{shade}` = light
 * `{1000 − shade}`), on both web and native (NativeWind reads the same `.dark`
 * block). So the light tint classes below resolve to the correct dark values
 * automatically — no `dark:` swaps needed (same rationale as Button). Verified
 * token-for-token against Figma's dark frame (node 2787:31842): every
 * fill / border / text value here maps to the inverted shade exactly (e.g.
 * `bg-secondary-50` → #0a0a0a, `border-orange-600` → #ff8904). Icons are the
 * ONE exception — they take a `color` prop, not a class, so CSS-var inversion
 * can't reach them; their dark tone is resolved explicitly in Chip.icons.ts.
 */
export const chipVariants = cva(
  'group relative inline-flex flex-row items-center justify-center gap-1.5 border ' +
    'web:backdrop-blur-[7.5px] web:shadow-chip ' +
    'web:transform-gpu ' +
    'web:transition-[transform,background-color,border-color,box-shadow,opacity] web:duration-150 web:ease-out ' +
    'web:enabled:active:scale-[0.97] web:enabled:active:shadow-none ' +
    'web:focus-visible:ring-1 web:focus-visible:ring-offset-1',
  {
    variants: {
      tint: {
        // Unselected gray chip.
        //   rest + hover : secondary-50. Figma's Default (1439:12700) and Hover
        //     (1435:12645) BOTH fill with `brand/secondary/50` — hover is not a
        //     tone step, so there is no `hover:` bg override here. (Figma's
        //     secondary/50 resolves to #fafafa; our compressed scale maps
        //     secondary-50 → #ffffff — a ~5/255 lift that is imperceptible and,
        //     as a bonus, keeps the fill distinct from the app's #fafafa page
        //     background. In dark mode secondary-50 inverts to #0a0a0a, which
        //     matches Figma's dark Default exactly.)
        //   pressed : darkens to secondary-100 (Figma 1435:12652), on top of
        //     the shared base's scale-0.97 + shadow-drop.
        //   focused : secondary-900 ring only. The inner border stays
        //     secondary-200 — Figma's Focused node (1435:12630) keeps the
        //     resting border; it does NOT lighten.
        neutral:
          'bg-secondary-50 border-secondary-200 ' +
          'enabled:active:bg-secondary-100 ' +
          // Dark: a CONTROL, one step above its host (elevation.ts) — the
          // mirrored -50 is the chrome colour on the current ramp.
          `${DARK_CONTROL_TOGGLED} web:dark:enabled:active:bg-secondary-200/80 ` +
          'web:focus-visible:ring-secondary-900 ',
        // Selected / New — tertiary (blue). Border is the lighter -500
        // (Figma node 1455:14335), unlike the other colored tints which use
        // -600. Per the design the colored chips are defined only at rest — no
        // per-tint hover/pressed (interaction states live on `neutral`); the
        // uniform press scale + the focus ring come from the shared base.
        new:
          'bg-tertiary-50 border-tertiary-500 ' + 'web:focus-visible:ring-secondary-900 ',
        // Selected / Review — orange.
        review:
          'bg-orange-50 border-orange-600 ' + 'web:focus-visible:ring-secondary-900 ',
        // Selected / Memorised — green.
        memorised:
          'bg-green-50 border-green-600 ' + 'web:focus-visible:ring-secondary-900 ',
        // Selected / To review — amber.
        toReview:
          'bg-amber-50 border-amber-600 ' + 'web:focus-visible:ring-secondary-900 ',
        // Selected / Starred (favourite) — amber + a Star icon supplied by the
        // caller. Not part of the Figma "Filter chips" progress-tint set (which
        // is NA/New/Review/Memorised/To review); added for the app's favourite
        // filter. Shares the amber ramp with `toReview` by design — callers that
        // show both in one bar use `review` (orange) for to-review so the two
        // stay distinct (see flashka-web deck filter bar).
        starred:
          'bg-amber-50 border-amber-600 ' + 'web:focus-visible:ring-secondary-900 ',
        // Selected / Primary — the brand tint, for chips whose selection is a
        // plain "picked" rather than a progress state (onboarding's way-of-
        // studying picker). App extension like `starred` — not in the Figma
        // progress-tint set; follows the same -50/-600 ramp pattern.
        primary:
          'bg-primary-50 border-primary-600 ' + 'web:focus-visible:ring-secondary-900 ',
        // Selected / feature hues — for chips that stand for a coloured
        // FEATURE (onboarding's way-of-studying picker: the selected chip
        // takes its icon's hue). The other hues already exist as progress
        // tints (green = memorised, orange = review, amber = toReview, blue =
        // new); these three complete the brand palette on the same ramp.
        pink: 'bg-pink-50 border-pink-600 ' + 'web:focus-visible:ring-secondary-900 ',
        rose: 'bg-rose-50 border-rose-600 ' + 'web:focus-visible:ring-secondary-900 ',
        lime: 'bg-lime-50 border-lime-600 ' + 'web:focus-visible:ring-secondary-900 ',
      },
      // Per-size geometry from the Figma per-size bands. Height is exact
      // (border-box). Radius is a flat per-size value (no bevel asymmetry, so
      // no chip-radii machinery). `py` is omitted — the fixed height +
      // `items-center` already centers content; the pl value doubles as the
      // symmetric padding reference for the removable compounds below.
      size: {
        xl: 'h-11 pl-2.5 pr-4 rounded-[16px]',
        l: 'h-10 pl-2 pr-3.5 rounded-[14px]',
        m: 'h-9 pl-2 pr-3 rounded-[12px]',
        s: 'h-8 pl-2 pr-2.5 rounded-[11px]',
      },
      removable: { true: '', false: '' },
      disabled: { true: '', false: '' },
    },
    compoundVariants: [
      // Removable → symmetric padding: the right padding collapses to match
      // the left (Figma `Removable=Yes` nodes use `p-{pl}`), making room read
      // evenly around the trailing circle-x.
      { removable: true, size: 'xl', class: 'pr-2.5' },
      { removable: true, size: 'l', class: 'pr-2' },
      { removable: true, size: 'm', class: 'pr-2' },
      { removable: true, size: 's', class: 'pr-2' },
    ],
    defaultVariants: {
      tint: 'neutral',
      size: 'm',
      removable: false,
      disabled: false,
    },
  },
);

/**
 * Text classes per tint + size. Applied to the inner `<Text>` wrapping a
 * string child; also colors icons that read `currentColor` on web.
 * `!leading-[normal]` lets RN's Text use font metrics (matches web, avoids
 * native ascender clipping) — line height is visually irrelevant for the
 * single-line, height-fixed chip.
 */
export const chipTextVariants = cva('whitespace-nowrap font-medium !leading-[normal]', {
  variants: {
    tint: {
      neutral: 'text-secondary-600 ',
      new: 'text-tertiary-600 ',
      review: 'text-orange-600 ',
      memorised: 'text-green-600 ',
      toReview: 'text-amber-600 ',
      starred: 'text-amber-600 ',
      primary: 'text-primary-600 ',
      pink: 'text-pink-600 ',
      rose: 'text-rose-600 ',
      lime: 'text-lime-600 ',
    },
    // Typography per Figma per-size band: XL 16px (text-base), L/M 14px
    // (text-sm), S 12px (text-xs). `native:font-mona-medium` pins the
    // per-weight .ttf on native (no-op on web, where the variable font
    // respects `font-medium`).
    size: {
      xl: 'text-base native:font-mona-medium',
      l: 'text-sm native:font-mona-medium',
      m: 'text-sm native:font-mona-medium',
      s: 'text-xs native:font-mona-medium',
    },
    disabled: { true: '', false: '' },
  },
  defaultVariants: { tint: 'neutral', size: 'm', disabled: false },
});

export type ChipVariantProps = VariantProps<typeof chipVariants>;
